import React from 'react'
import { Button, LinkButton } from '@/atoms/Button'
import { AsH1, EyebrowMD, ParagraphMD, TitleLG } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { LivestreamViewership } from '@/organisms/LivestreamInformation/LivestreamViewership'
import { useSocialSharing } from '@/organisms/SocialSharing'
import { LivestreamMeta } from '@/services/LivestreamService'
import { ProjectMeta } from '@/services/ProjectsService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface LivestreamInformationProps {
  livestreamMeta: LivestreamMeta
  projectMeta: ProjectMeta
  hideShare?: boolean
}

export const LivestreamInformation: React.FC<LivestreamInformationProps> = ({
  livestreamMeta,
  projectMeta,
  hideShare,
}) => {
  const { asPath } = useSafeRouter()
  const { t } = useTranslate('livestream')
  const { links } = useSocialSharing({ shareUrl: `${paths.getBaseURL()}${asPath}` })

  const logoCloudinaryPath = projectMeta.logoCloudinaryPath
  const projectName = projectMeta.name
  const livestreamName = livestreamMeta.name
  const livestreamDescription = livestreamMeta.description

  return (
    <div className="w-full">
      <div className="mb-4 flex items-end justify-between gap-2">
        {logoCloudinaryPath && (
          <img className="max-h-[68px]" alt={projectName} src={getCloudinaryImageUrl({ path: logoCloudinaryPath })} />
        )}
      </div>
      <TitleLG weight="bold" as={AsH1} className="mb-2 w-full lg:w-2/3">
        {livestreamName}
      </TitleLG>

      <LivestreamViewership startAt={livestreamMeta.startAt} />

      {!hideShare && (
        <div className="mb-12 flex items-center gap-2">
          <EyebrowMD weight="bold" color="core-gray-600">
            <Translate t={t} i18nKey="share">
              Share
            </Translate>
          </EyebrowMD>
          {links.map(({ icon: Icon, href, onClick, name }) => {
            if (href) {
              return (
                <LinkButton
                  key={name}
                  external
                  href={href}
                  variant="core-gray-900"
                  className="h-8 w-8 rounded-full p-0"
                  aria-label={name}
                >
                  <div className="mx-auto">
                    <Icon color="white" size={18} />
                  </div>
                </LinkButton>
              )
            } else if (onClick) {
              return (
                <Button
                  key={name}
                  variant="core-gray-900"
                  className="h-8 w-8 rounded-full p-0"
                  aria-label={name}
                  onClick={onClick}
                >
                  <div className="mx-auto">
                    <Icon color="white" size={18} />
                  </div>
                </Button>
              )
            }
          })}
        </div>
      )}

      <ParagraphMD className="mb-4 w-full lg:w-2/3" color="core-gray-300">
        {livestreamDescription}
      </ParagraphMD>
    </div>
  )
}
